import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentSalesOrder } from '@/providers/SalesOrderProvider';
import { useSalesOrderAccountings, useSalesOrderAccountingsInvalidate } from '@/hooks/api/1390_sales_order_accountings';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { SalesOrderAccounting } from '@/hooks/api/models';
import { useDestroySalesOrderAccounting } from '@/hooks/api/1420_destroy_sales_order_accounting';
import { ModalConfirm } from '@/components/element/modal/ModalConfirm';
import { useUpdateSalesOrderAccounting } from '@/hooks/api/1410_update_sales_order_accountings';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { toastApiError } from '@/lib/toast';
import { SalesOrderAccountingLogic, SalesOrderLogic } from '@/hooks/api/models/logics';
import { Link } from 'react-router-dom';
import { useSalesOrderInvalidate } from '@/hooks/api/1260_sales_order';
import { InputPrice } from '@/components/element/Form/InputPrice';

export const SalesOrderAccountingsElement = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const { data: accountings } = useSalesOrderAccountings({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const [delTarget, setDelTarget] = useState<SalesOrderAccounting>();
  const {
    mutate: destroyMutate,
    isSuccess: destroyIsSuccess,
    isPending: destroyIsPending,
  } = useDestroySalesOrderAccounting();
  const { invalidate } = useSalesOrderAccountingsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { invalidate: invalidateSalesOrder } = useSalesOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });

  useEffect(() => {
    if (destroyIsSuccess) {
      invalidate();
      invalidateSalesOrder();
    }
  }, [destroyIsSuccess]);

  useEffect(() => setShowLoading(destroyIsPending), [destroyIsPending]);

  const onDeleteClick = () => {
    setShowLoading(true);
    destroyMutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: _so_!.id,
      sales_order_accounting_id: delTarget!.id,
    });
  };

  return (
    <div className="table-responsive">
      <ModalConfirm
        modalId={`delSalesOrderAccountingConfirm`}
        okClicked={() => onDeleteClick()}
        message={'削除します。よろしいですか？'}
      />

      <table className="table align-items-center">
        <thead>
          <tr>
            <th
              className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end"
              style={{ width: 80 }}
            >
              計上月
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end">
              一括請求金額（税抜）
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end">
              月額請求金額（税抜）
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end">合計</th>
            <th className="text-secondary opacity-7 text-end" style={{ width: 110 }}></th>
          </tr>
        </thead>
        <tbody>
          {accountings?.map((d) => <SalesOrderAccountingRow key={`soa-${d.id}`} d={d} setDelTarget={setDelTarget} />)}
          <tr>
            <td className="text-xs text-dark font-weight-bold text-end">合計金額</td>
            <td className="text-xs text-dark font-weight-bold text-end">
              ¥{SalesOrderAccountingLogic.calcTotalSpotAmount(accountings || []).toLocaleString()}
            </td>
            <td className="text-xs text-dark font-weight-bold text-end">
              ¥{SalesOrderAccountingLogic.calcTotalMonthlyAmount(accountings || []).toLocaleString()}
            </td>
            <td className="text-xs text-dark font-weight-bold text-end">
              ¥{SalesOrderAccountingLogic.calcTotalAmount(accountings || []).toLocaleString()}
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

type FormInputs = {
  identifier: string;
  team_id: number;
  sales_order_id: number;
  sales_order_accounting_id: number;
  monthly_amount?: number;
  spot_amount?: number;
};

const SalesOrderAccountingRow = ({
  d,
  setDelTarget,
}: {
  d: SalesOrderAccounting;
  setDelTarget: Dispatch<SetStateAction<SalesOrderAccounting | undefined>>;
}) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const [editing, setEditing] = useState<boolean>(false);
  const [editable, setEditable] = useState<boolean>(false);
  const { mutate, data, error, isPending } = useUpdateSalesOrderAccounting();
  const { invalidate } = useSalesOrderAccountingsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { invalidate: invalidateSalesOrder } = useSalesOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: _so_!.id,
      sales_order_accounting_id: d.id,
      spot_amount: d.spot_detail?.amount,
      monthly_amount: d.monthly_detail?.amount,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);

  const onSubmit: SubmitHandler<FormInputs> = (inputData) => {
    setShowLoading(true);
    mutate(inputData);
  };

  useEffect(() => {
    if (data) {
      invalidate();
      invalidateSalesOrder();
      setEditing(false);
    }
  }, [data]);

  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    setEditable(_so_!.accounting_manual_mode && SalesOrderLogic.canUpdateAccounting(_t_!, me!));
  }, [_so_!.accounting_manual_mode]);

  if (!editing) {
    return (
      <tr>
        <td className="text-xs text-dark text-end">{d.year_month}</td>
        <td className="text-xs text-dark text-end">¥{SalesOrderAccountingLogic.spotAmount(d).toLocaleString()}</td>
        <td className="text-xs text-dark text-end">¥{SalesOrderAccountingLogic.monthlyAmount(d).toLocaleString()}</td>
        <td className="text-xs text-dark text-end">¥{SalesOrderAccountingLogic.totalAmount(d).toLocaleString()}</td>
        <td className="text-end">
          <Link
            to=""
            className={`btn btn-icon-only bg-gradient-success me-1 ${!editable && 'disabled'}`}
            onClick={() => setEditing((e) => !e)}
          >
            <i className="fas fa-edit"></i>
          </Link>
          <Link
            to=""
            className={`btn btn-icon-only bg-gradient-danger ${!editable && 'disabled'}`}
            data-bs-toggle="modal"
            data-bs-target={`#delSalesOrderAccountingConfirm`}
            onClick={() => setDelTarget(d)}
          >
            <i className="fas fa-trash-alt"></i>
          </Link>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="text-xs text-end">{d.year_month}</td>
      <td className="text-xs text-wrap">
        <div className="input-group">
          <Controller
            render={({ field: { onChange, value, ...field } }) => <InputPrice onChange={onChange} value={value} />}
            control={control}
            name="spot_amount"
          />
        </div>
      </td>
      <td className="text-xs text-wrap">
        <div className="input-group">
          <Controller
            render={({ field: { onChange, value, ...field } }) => <InputPrice onChange={onChange} value={value} />}
            control={control}
            name="monthly_amount"
          />
        </div>
      </td>
      <td className="text-xs text-dark text-end">¥{SalesOrderAccountingLogic.totalAmount(d).toLocaleString()}</td>
      <td className="text-end">
        <button className="btn btn-icon-only btn-outline-secondary me-1" onClick={() => setEditing((e) => !e)}>
          <i className="fas fa-undo"></i>
        </button>
        <button className="btn btn-icon-only bg-gradient-dark" onClick={() => handleSubmit(onSubmit)()}>
          <i className="fas fa-check-circle"></i>
        </button>
      </td>
    </tr>
  );
};
